import { feature as createFeature, featureCollection } from '@turf/turf';
import { transform } from 'ol/proj';
import { ClientWithAuth } from 'client/services/service';
import { isNi } from 'client/utils/coords/coordUtils';
import { LINE_STRING, MULTI_LINE_STRING } from 'client/utils/constants/geometryType';

const REGEX_ADDRESS = /[a-z]/i; // Contains at least one letter
const REGEX_UPRN = /^\d{1,12}$/; // Up to 12 digit reference
const REGEX_USRN = /^\d{6,8}$/; // Unique 6 to 8 digit reference
const REGEX_TOID = /^(osgb)(\d{13,16})$/i; // OSGB (case insensitive) followed by 13 or 16 digits

const matchesAddress = input => REGEX_ADDRESS.test(input);
const matchesUPRN = input => REGEX_UPRN.test(input);
const matchesUSRN = input => REGEX_USRN.test(input);
const matchesTOID = input => REGEX_TOID.test(input);

const epsgToCrs = (epsg) => `urn:ogc:def:crs:EPSG::${epsg}`;

// Add coordinate reference system
const addCrs = (geoJson, crs) => {
    geoJson.crs = {
        type: 'name',
        properties: { name: crs }
    };
    return geoJson;
};

const createFeatureFromGeometry = (geomType, coordinates, searchType, displayText, crs) => {
    const properties = { type: searchType, displayText };
    const geometry = { type: geomType, coordinates };
    let feature = createFeature(geometry, properties);
    feature = addCrs(feature, crs);

    return feature;
};

const createFeatureCollection = (features, crs) => {
    let collection = featureCollection(features);
    collection = addCrs(collection, crs);
    return collection;
};

// Removes any features with duplicate display text
const removeDuplicateAddresses = (features) => {
    let filteredFeatures = [];
    if (features && features.length) {
        const matchingAddresses = [];
        filteredFeatures = features.filter(feature => {
            if (!matchingAddresses.includes(feature.properties.displayText)) {
                matchingAddresses.push(feature.properties.displayText);
                return true;
            } else {
                return false;
            }
        });
    }
    return filteredFeatures;
};

const getUPRNFeatures = async (searchValue, crs) => {
    let features = [];
    if (matchesUPRN(searchValue)) {
        const strippedLeadingZeros = searchValue.toString().replace(/^0+/, '');
        const countryCodeFilter = isNi(crs) ? 'COUNTRY_CODE:N' : 'COUNTRY_CODE:E COUNTRY_CODE:W';
        const params = { dataset: 'LPI', uprn: strippedLeadingZeros, fq: countryCodeFilter };
        const response = await ClientWithAuth.get('/os-search/uprn', { params });
        const { data } = response;
        if (data && data.results && data.results.length) {
            features = data.results.map(result => {
                const { ADDRESS: address, X_COORDINATE: x, Y_COORDINATE: y } = result.LPI;
                let xy2 = [x, y];
                if (crs !== 'urn:ogc:def:crs:EPSG::27700') {
                    xy2 = transform([x, y], 'EPSG:27700', crs);
                }
                return createFeatureFromGeometry('Point', xy2, 'UPRN', `UPRN: ${strippedLeadingZeros} - ${address}`, crs);
            });
        }
    }

    return features;
};

const getUSRNFeatures = async (searchValue, crs) => {
    let features = [];
    if (matchesUSRN(searchValue)) {
        const params = {
            service: 'WFS',
            request: 'GetFeature',
            version: '2.0.0',
            typeNames: 'osFeatures:OpenUSRN_USRN',
            srsName: crs,
            outputFormat: 'geojson',
            filter: `<ogc:Filter><ogc:PropertyIsEqualTo><ogc:PropertyName>USRN</ogc:PropertyName><ogc:Literal>${searchValue}</ogc:Literal></ogc:PropertyIsEqualTo></ogc:Filter>`
        };
        const response = await ClientWithAuth.get('/os-features/wfs', { params });
        const { data } = response;
        if (data.features.length < 2) {
            features = data.features.map(feature => {
                // Ad-hoc fix for ESRI WFS bug where feature with geometry of
                // MultiLineString may be returned as LineString
                if (feature.geometry.type === LINE_STRING && Array.isArray(feature.geometry.coordinates[0][0])) {
                    feature.geometry.type = MULTI_LINE_STRING;
                }
                return createFeatureFromGeometry(feature.geometry.type, feature.geometry.coordinates, 'USRN', 'USRN: ' + searchValue, crs);
            });
        } else {
            throw new Error('USRN search returned more than one value');
        }
    }
    return features;
};

const getTOIDFeatures = async (searchValue, crs) => {
    async function makeFilteredWfsRequest (typeNames, propertyName, propertyValue) {
        const params = {
            service: 'WFS',
            request: 'GetFeature',
            version: '2.0.0',
            typeNames: typeNames,
            srsName: crs,
            outputFormat: 'geojson',
            filter: `<ogc:Filter><ogc:PropertyIsEqualTo><ogc:PropertyName>${propertyName}</ogc:PropertyName><ogc:Literal>${propertyValue}</ogc:Literal></ogc:PropertyIsEqualTo></ogc:Filter>`
        };
        const response = await ClientWithAuth.get('/os-features/wfs', { params });
        const { data } = response;
        if (data?.features?.length === 1) {
            return data.features.map(feature => {
                // Ad-hoc fix for ESRI WFS bug where feature with geometry of
                // MultiLineString may be returned as LineString
                if (feature.geometry.type === LINE_STRING && Array.isArray(
                    feature.geometry.coordinates[0][0])) {
                    feature.geometry.type = MULTI_LINE_STRING;
                }
                return createFeatureFromGeometry(feature.geometry.type, feature.geometry.coordinates, propertyName, `${propertyName}: ` + searchValue, crs);
            });
        } else {
            return [];
        }
    }

    if (!matchesTOID(searchValue)) {
        return [];
    }

    searchValue = searchValue.toLowerCase(); // TOID prefix is always lower case https://www.wikiwand.com/en/TOID

    /**
     * OS Features API search implements an inclusive OR between all supplied typeNames and the filtered property,
     * hence a single request per typename.
     *
     * If OS Features filtering was implemented as: typeNames && filtered property
     * we could achieve the same results in a single request that includes all typeNames.
     */
    const highwayFeatures = makeFilteredWfsRequest('osFeatures:OpenTOID_HighwaysNetwork', 'TOID', searchValue);
    const siteFeatures = makeFilteredWfsRequest('osFeatures:OpenTOID_SitesLayer', 'TOID', searchValue);
    const topoFeatures = makeFilteredWfsRequest('osFeatures:OpenTOID_TopographyLayer', 'TOID', searchValue);

    const results = await Promise.all(
        [highwayFeatures, siteFeatures, topoFeatures]
    );

    return results.flat();
};

const getNIAddressFeatures = async (searchValue, crs) => {
    if (!matchesAddress(searchValue)) {
        return [];
    }
    const params = { dataset: 'DPA', query: searchValue, maxResults: 50, fq: 'COUNTRY_CODE:N' };
    const response = await ClientWithAuth.get('/os-search/findPlaces', { params });
    const { data } = response;
    if (!data?.results?.length) {
        return [];
    }

    return data.results.map(result => {
        const {
            X_COORDINATE: x,
            Y_COORDINATE: y,
            DEPENDENT_THOROUGHFARE_NAME: substreet,
            THOROUGHFARE_NAME: street,
            DEPENDENT_LOCALITY: locality,
            POST_TOWN: town,
            LOCAL_CUSTODIAN_CODE_DESCRIPTION: adminArea,
            POSTCODE: postcode,
            BUILDING_NAME: buildingName
        } = result.DPA;
        const displayedBuildingName = buildingName?.toLowerCase().includes(searchValue?.toLowerCase()) || searchValue.toLowerCase().includes(buildingName?.toLowerCase())
            ? buildingName
            : ''; // include building name if it is directly relevant to the search
        let displayPostcode;
        if (postcode && searchValue.includes(postcode)) {
            displayPostcode = postcode;
        } else if (postcode) {
            displayPostcode = postcode.substring(0, postcode.indexOf(' '));
        }
        const addressFeatures = [displayedBuildingName, substreet, street, locality, town, adminArea, displayPostcode];
        const displayText = addressFeatures.filter((section) => section?.length > 0).join(', ');
        const xy2 = transform([x, y], 'EPSG:27700', crs);
        return createFeatureFromGeometry('Point', xy2, 'Address', displayText, crs);
    });
};

const getAddressFeatures = async (searchValue, crs) => {
    if (!matchesAddress(searchValue)) {
        return [];
    }
    const params = { query: searchValue, maxResults: 50 };
    const response = await ClientWithAuth.get('/os-search/find', { params });
    const { data } = response;
    if (!data?.results?.length) {
        return [];
    }
    return data.results.map(result => {
        const {
            GEOMETRY_X: x,
            GEOMETRY_Y: y,
            NAME1: street,
            POPULATED_PLACE: town,
            COUNTY_UNITARY: adminArea,
            POSTCODE_DISTRICT: postcode
        } = result.GAZETTEER_ENTRY;
        const addressFeatures = [street, town, adminArea, postcode];
        const displayText = addressFeatures.filter((section) => section?.length > 0).join(', ');

        return createFeatureFromGeometry('Point', [x, y], 'Address', displayText, crs);
    });
};

const searchResults = (searchValue, epsg) => {
    const crs = epsgToCrs(epsg);

    // Order matters here, we want address features last in the list.
    const results = [getUPRNFeatures(searchValue, crs)];
    if (isNi(epsg)) {
        results.push(getNIAddressFeatures(searchValue, crs));
    } else {
        results.push(getUSRNFeatures(searchValue, crs));
        results.push(getTOIDFeatures(searchValue, crs));
        results.push(getAddressFeatures(searchValue, crs));
    }

    return results;
};

export default {
    getSearchAsync: async (searchValue, epsg = '27700') => {
        const results = await Promise.all(searchResults(searchValue, epsg));

        // Flatten & dedupe
        let features = removeDuplicateAddresses(results.flat());

        /*
         * FIXME: This 'pagination' works but only on the aggregated data.
         *  Since the returned data from the APIs themselves may be
         *  paginated (and may contain duplicates), the result set above is
         *  inherently incomplete unless all upstream pages are queried.
         */
        features = features.slice(0, 10); // return maximum of 10 unique address results

        const crs = epsgToCrs(epsg);
        return createFeatureCollection(features, crs);
    }
};
